import { lazy } from "react";
import {
  Navigate,
  Outlet,
  Route,
  useLocation,
  useParams,
} from "react-router-dom";
import MainLayout from "@/components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "@/components/PermissionGuard/PermissionGuard";
import {
  coachesLearningUrl,
  type CoachesLearningTab,
} from "@/modules/learningCenter/features/CoachesLearning/coachesTab";

// Lazy-loaded route components
const PathEngagementDashboard = lazy(
  () =>
    import(
      "@/modules/coaching/features/LearningPathsDashboard/components/PathEngagementDashboard/PathEngagementDashboard"
    ),
);

const LearnerPage = lazy(
  () => import("@/modules/coaching/features/Learner/components/LearnerPage"),
);

const LearningPathDetail = lazy(
  () =>
    import(
      "@/modules/learningCenter/features/LearningPaths/components/LearningPathDetail/LearningPathDetail"
    ),
);

/**
 * The coach's list views (board, members, companies, paths, reviews) are
 * tabs of one page, Coaches Progress Tracking. The old standalone list
 * routes redirect to their tab and keep their query (board filters, the
 * drawer's ?enrollment=, ?assign=1), so every back control on a detail page
 * can point at that page and land where the coach started.
 */
const TabRedirect = ({ tab }: { tab: CoachesLearningTab }) => {
  const { search } = useLocation();
  return (
    <Navigate
      replace
      to={coachesLearningUrl(tab, new URLSearchParams(search))}
    />
  );
};

/** Coaching pages are learning-path pages: brand typeface (Manrope). */
const BrandFontOutlet = () => (
  <div className="contents font-brand">
    <Outlet />
  </div>
);

// Create stable component reference
const CoachingMainLayout = () => <MainLayout section="coaching" />;

/** The path analytics page used to live at /engagement; keep old links working. */
const LegacyEngagementRedirect = () => {
  const { pathId } = useParams();
  return (
    <Navigate replace to={`/coaching/learning-paths/${pathId}/analytics`} />
  );
};

const CoachingRoutes = (
  <Route
    element={<AuthenticationGuard component={CoachingMainLayout} />}
    path="/coaching"
  >
    <Route element={<BrandFontOutlet />}>
      <Route element={<TabRedirect tab="companies" />} index />
      <Route element={<TabRedirect tab="companies" />} path="dashboard" />
      {/* Member board: every enrollment the coach owns, by status (Approach A
        of docs/learning-center/coach-experience-review.md). */}
      <Route element={<TabRedirect tab="board" />} path="board" />
      {/* One learner on one path: the action-first page behind a board card. */}
      <Route
        element={
          <PermissionGuard requiredRoles={["ROLE_COACH", "ROLE_SUPER_ADMIN"]}>
            <LearnerPage />
          </PermissionGuard>
        }
        path="board/:enrollmentId"
      />
      <Route element={<TabRedirect tab="paths" />} path="learning-paths" />
      <Route element={<TabRedirect tab="reviews" />} path="reviews" />
      {/* Param is :id (not :pathId) because LearningPathDetail reads
        useParams<{ id }> — it is shared with /hub/learning-center/paths/:id. */}
      <Route
        element={
          <PermissionGuard requiredRoles={["ROLE_COACH", "ROLE_SUPER_ADMIN"]}>
            <LearningPathDetail />
          </PermissionGuard>
        }
        path="learning-paths/:id"
      />
      <Route
        element={
          <PermissionGuard requiredRoles={["ROLE_COACH", "ROLE_SUPER_ADMIN"]}>
            <PathEngagementDashboard />
          </PermissionGuard>
        }
        path="learning-paths/:pathId/analytics"
      />
      <Route
        element={<LegacyEngagementRedirect />}
        path="learning-paths/:pathId/engagement"
      />
    </Route>
  </Route>
);

export default CoachingRoutes;
